import { Grid, Typography, GridProps, Stack, TypographyProps } from '@mui/material';
import { MouseEventHandler, useState, createContext, useContext } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Linkable } from '@/components/blocks/Linkable';

interface OpenContextType {
	open: string;
	handleOnClick: MouseEventHandler<HTMLDivElement>;
}

const OpenContext = createContext<OpenContextType>({} as OpenContextType);

export const OpenProvider = ({ children }: { children: React.ReactNode }) => {
	const [open, setOpen] = useState('');

	const handleOnClick: MouseEventHandler<HTMLDivElement> = (event) => {
		const id = event.currentTarget.id;
		setOpen((prev) => (prev === id ? '' : id));
	};

	return <OpenContext.Provider value={{ open, handleOnClick }}>{children}</OpenContext.Provider>;
};

export const useOpen = () => {
	const context = useContext(OpenContext);
	if (context === undefined) {
		throw new Error('Using DpTabLinksContainer outside of its provider');
	}
	return context;
};

export const DpTabLinksContainer = ({ children }: { children: React.ReactNode }) => (
	<OpenProvider>
		<Grid sx={{ borderBottom: '1px solid #D9D9D9' }}>{children}</Grid>
	</OpenProvider>
);

export const DpTabLink = ({ id, children, component, ...props }: GridProps) => {
	const { open, handleOnClick } = useOpen();
	return (
		<Grid
			id={id}
			item
			display="flex"
			alignItems="center"
			justifyContent="flex-start"
			padding="0px 0px 10px 0px"
			onClick={handleOnClick}
			{...props}
		>
			<Typography
				component={component ?? 'h3'}
				sx={{
					fontSize: 'var(--font-size-sm)',
					fontWeight: '800',
					textAlign: 'center',
					justifyContent: 'center',
					textTransform: 'uppercase',
					color: id === open ? '#2C2C2C' : !open ? '#2C2C2C' : '#00000046',
				}}
			>
				{children}
			</Typography>
			<ExpandMoreIcon
				sx={{
					height: '24px',
					rotate: open === id ? '180deg' : '0deg',
					transition: 'transform 0.3s',
					color: id === open ? '#2C2C2C' : !open ? '#2C2C2C' : '#00000046',
				}}
			/>
		</Grid>
	);
};

export const DpTabLinkContent = ({ id, children }: { id: string; children: React.ReactNode }) => {
	const { open } = useOpen();
	return (
		<Stack
			component="ul"
			sx={{
				display: open === id ? 'flex' : 'none',
				padding: '0',
				gap: 1,
			}}
		>
			{children}
		</Stack>
	);
};

type DpTabLinkContentLinkProps = TypographyProps & {
	href?: string;
};

export const DpTabLinkContentLink = ({ href, children }: DpTabLinkContentLinkProps) => (
	<Linkable href={href}>
		<Typography
			component="li"
			sx={{
				listStyleType: 'none',
				fontSize: 'var(--font-size-sm)',
				fontWeight: 'bold',
			}}
		>
			{children}
		</Typography>
	</Linkable>
);
